<template>
    <DataTable :value="siteLinks" scrollable :scrollHeight="'90vh'" class="w-full" :sortField="'approvedAt'"
        :sortOrder="-1" :loading="linksLoading">
        <Column field="siteName" :header="headerName" :headerStyle="headerStyles">
            <template #body="slotProps">
                {{ slotProps.data.siteName }}
            </template>
        </Column>

        <Column field="advertiserName" header="Advertiser" :headerStyle="headerStyles">
            <template #body="slotProps">
                {{ slotProps.data.advertiserName }}
            </template>
        </Column>
        <Column field="publisherNote" header="Internal Code" :headerStyle="headerStyles">
            <template #body="slotProps">
                <div v-if="slotProps.data.publisherNote || editingAliasIds.has(slotProps.data.linkId)"
                    class="flex items-center gap-2">
                    <template v-if="editingAliasIds.has(slotProps.data.linkId)">
                        <InputText v-model="newAliases[slotProps.data.linkId]" class="p-inputtext-sm w-24"
                            placeholder="Enter alias" @keyup.enter="submitAlias(slotProps.data.linkId)" />
                        <div class="flex gap-1">
                            <Button icon="pi pi-check" @click="submitAlias(slotProps.data.linkId)" severity="primary"
                                size="small" class="p-button-sm" />
                            <Button icon="pi pi-times" @click="cancelEditAlias(slotProps.data.linkId)"
                                severity="secondary" size="small" class="p-button-sm" />
                        </div>
                    </template>
                    <div v-else class="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                        @click="startEditExistingAlias(slotProps.data.linkId, slotProps.data.publisherNote)">
                        {{ slotProps.data.publisherNote }}
                    </div>
                </div>
                <Button v-else icon="pi pi-plus" @click="startEditAlias(slotProps.data.linkId)" severity="secondary"
                    size="small" class="p-button-sm p-button-outlined" v-tooltip.bottom="'Add Internal Code'" />
            </template>
        </Column>

        <Column field="affiliateLink" header="Affiliate Link" :headerStyle="headerStyles">
            <template #body="slotProps">
                <div class="flex" v-if="slotProps.data.affiliateLink">
                    <div class="aff-link cursor-pointer flex items-center gap-2">
                        <a :href="slotProps.data.affiliateLink" target="_blank" rel="noopener noreferrer"
                            class="hover:underline aff-link">
                            {{ slotProps.data.affiliateLink }}
                        </a>
                        <Button icon="pi pi-copy" text rounded size="small" class="aff-link"
                            v-tooltip.bottom="'Copy link'"
                            @click.stop="copyToClipboard(slotProps.data.affiliateLink)" />
                    </div>
                </div>
            </template>
        </Column>
        <Column header="Postbacks" :headerStyle="headerStyles">
            <template #body="{ data }">
                <Button  @click="openPostbackModal(data)" :label="postbackLabel(data)"
                    :severity="(data.ftdPostback) ? 'primary' : 'secondary'"
                    v-tooltip.top="data.ftdPostback ? 'View Postback' : 'Set Postback'"
                    :outlined="(data.ftdPostback) ? false : true" />
            </template>
        </Column>
        <Column field="deal" header="Deal" :headerStyle="headerStyles">
            <template #body="slotProps">
                {{ getFriendlyDealMessage(slotProps.data.deal) }}
            </template>
        </Column>
        <Column field="status" header="Status" :headerStyle="headerStyles">
            <template #body="slotProps">
                <Tag :value="slotProps.data.status" :severity="Utils.getLinkStatusSeverity(slotProps.data.status)" />
            </template>
        </Column>
        <Column field="approvedAt" header="Approved At" :headerStyle="headerStyles" sortable>
            <template #body="slotProps">
                {{ formatDateTime(slotProps.data.approvedAt) }}
            </template>
        </Column>
    </DataTable>
    <div class="justify-center items-center pt-8">
        <div class="w-full  rounded-lg shadow-md">
        </div>
        <Postbacks v-model:visible="showPostbackModal" :siteLink="selectedSiteLink"
            :affiliateSytem="selectedLinkAffiliateSystem" @save="handlePostbackSave" />
    </div>
</template>


<script setup>
import { shallowRef, onMounted, ref, computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import SiteLinkService from '@/services/SiteLinkService';
import { headerStyles } from '@/global/headerStyles';
import { getFriendlyDealMessage } from '@/utils/deals';
import Utils from '@/utils';
import { useStore } from 'vuex';
import Postbacks from './PostbackDetails.vue';

const isAdmin = computed(() => store.getters.isAdmin);
const isImpersonated = computed(() => store.getters.isImpersonated);
const toast = useToast();
const siteLinks = shallowRef([]);
const editingAliasIds = ref(new Set());
const newAliases = ref({});
const store = useStore()
const isSeo = computed(() => store.getters.isSeo);
const headerName = computed(() => isSeo.value ? 'Site' : 'Toplist');
const showPostbackModal = ref(false);
const selectedSiteLink = ref(null);
const selectedLinkAffiliateSystem = ref(null);

const linksLoading = ref(true);

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.add({
            severity: 'success',
            summary: 'Copied!',
            detail: 'Link copied to clipboard',
            life: 3000
        });
    } catch (err) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to copy to clipboard',
            life: 3000
        });
    }
};

const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true // This enables AM/PM format
    });
};

const startEditExistingAlias = (linkId, currentAlias) => {
    editingAliasIds.value.add(linkId);
    newAliases.value[linkId] = currentAlias; // Pre-fill with existing alias
};

const startEditAlias = (linkId) => {
    editingAliasIds.value.add(linkId);
    newAliases.value[linkId] = '';
};

const cancelEditAlias = (linkId) => {
    editingAliasIds.value.delete(linkId);
    delete newAliases.value[linkId];
};

const submitAlias = async (linkId, advertiserId) => {
    await SiteLinkService.updateSiteBrandLinkAlias(linkId, newAliases.value[linkId]);
    toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Alias updated successfully',
        life: 3000
    });
    cancelEditAlias(linkId);
    emit('refresh');
};

const openPostbackModal = (siteLink) => {
    selectedSiteLink.value = siteLink;
    showPostbackModal.value = true;
    selectedLinkAffiliateSystem.value = siteLink.affiliateSystem;
};

const handlePostbackSave = async () => {
    //for now lets refresh dt source
    await fetchSiteLinks();
};

const postbackLabel = (data) => {
    const hasFTD = !!data.ftdPostback;
    const hasReg = !!data.registerPostback;

    if (hasFTD && hasReg) return 'FTD/REG';
    if (hasFTD) return 'FTD';
    if (hasReg) return 'REG';
    return 'Not Set';
};

const fetchSiteLinks = async () => {
    try {
        const response = await SiteLinkService.getAll();
        siteLinks.value = response.data.filter(link => link.status !== 'deleted');
    } catch (error) {
        console.error(error);
    } finally {
        linksLoading.value = false;
    }
};

onMounted(() => {
    fetchSiteLinks();
});
</script>

<style scoped>
.aff-link {
    color: rgb(213, 179, 40);
    text-decoration: underline;
}

.aff-link:hover {
    color: rgb(213, 179, 40) !important;
    text-decoration: underline;
    background-color: transparent !important;
}
</style>